import { faltaDeEmbalagem } from '../produtos/embalagem'
import type { ComissaoPercentual, Preco, Produto, RegistroBase, RegraComissao, TabelaPreco } from '../db/tipos'
import type { TabelaSync } from '../db/banco'
import { pctParaProduto } from '../comissoes/motor'
import { converterComissao } from '../importacao/validacao'
import { precoDigitado, precoAvulsoDigitado } from './entradaPreco'
import { produtosParaEditarPreco } from './listagemTabela'
import { regrasEmRascunho, numeroParaCampo, MSG_PCT_FIXO_VAZIO, type RegraRascunho } from '../representadas/rascunhoDaPolitica'

// Tela Editar da tabela (Leva 2, spec 2026-09-29): tudo e RASCUNHO ate o
// "Salvar alteracoes". Este arquivo decide o que mostrar, o que recusar e o
// que gravar -- a tela so desenha. Mesmo molde de rascunhoDaPolitica.ts.

export interface EdicaoDaLinha { preco?: string; avulso?: string; comissao?: string }
export type EdicoesDaTabela = Record<string, EdicaoDaLinha>
export interface QuadroDaTabela {
  ativa: boolean
  condicoes: string[] // ids de condicoes SALVAS; vazio = vale em todas
  regra: RegraRascunho | null // null = usa a comissao da fornecedora
}
export interface SalvoDaTabela {
  tabela: TabelaPreco
  regras: RegraComissao[] // vivas, da fornecedora inteira
  percentuais: ComissaoPercentual[] // vivos, dessas regras
  precos: Preco[] // vivos, desta tabela
  produtos: Produto[] // catalogo vivo da fornecedora
}
export interface GravacaoDaTabela { tabela: TabelaSync; registro: Partial<TabelaPreco | RegraComissao | ComissaoPercentual | Preco> & { id: string } }
export type FiltroDaTabela = 'nesta' | 'fora' | 'sem_comissao'
type Novo = () => Pick<RegistroBase, 'id' | 'criado_em' | 'atualizado_em' | 'apagado_em'>

export const CHAVE_QUADRO = 'quadro'
export const MSG_COMISSAO = 'Informe a comissão (0 a 100).'
export const MSG_PRECO_VAZIO = 'Informe o preço ou tire o produto da tabela.'
export const MSG_PRECO_INVALIDO = 'Preço inválido.'
export const MSG_AVULSO_INVALIDO = 'Preço avulso inválido.'

const regraPropria = (s: SalvoDaTabela) => s.regras.find((r) => r.apagado_em === null && r.tabela_preco_id === s.tabela.id)
const regraDaFornecedora = (s: SalvoDaTabela) => s.regras.find((r) => r.apagado_em === null && r.tabela_preco_id === null)
const vivosDa = (s: SalvoDaTabela, regraId: string) => s.percentuais.filter((p) => p.regra_id === regraId && p.apagado_em === null)
const pctOuNulo = (texto: string) => (texto.trim() === '' ? null : converterComissao(texto))

export function quadroInicial(s: SalvoDaTabela): QuadroDaTabela {
  const propria = regraPropria(s)
  return {
    ativa: s.tabela.ativa,
    condicoes: [...(s.tabela.condicoes_validas ?? [])],
    regra: propria ? regrasEmRascunho([propria]).find((r) => r.id === propria.id)! : null,
  }
}

// Regra propria NOVA copia a da fornecedora (decisao dela 29/09, opcao a):
// assim nenhum produto desta tabela muda de % so por ela ter nascido.
export function regraPropriaCopiandoFornecedora(s: SalvoDaTabela): RegraRascunho {
  const f = regraDaFornecedora(s)
  return {
    chave: `nova-${s.tabela.id}`, id: null, tabelaPrecoId: s.tabela.id,
    tipo: f?.tipo ?? 'pct_por_item', pctPadrao: numeroParaCampo(f?.pct_padrao),
    gatilho: f?.gatilho_liberacao ?? 'faturamento', removida: false,
  }
}

// A regra que o QUADRO descreve agora, com os percentuais que ela teria:
// os da propria (se ja existe) ou os da fornecedora (que serao copiados).
function emVigor(s: SalvoDaTabela, q: QuadroDaTabela): { regra: RegraComissao; percentuais: ComissaoPercentual[] } | null {
  const forn = regraDaFornecedora(s)
  if (q.regra === null) return forn ? { regra: forn, percentuais: vivosDa(s, forn.id) } : null
  const fonte = q.regra.id !== null ? regraPropria(s) : forn
  const regra = {
    id: fonte?.id ?? '', representada_id: s.tabela.representada_id, tabela_preco_id: s.tabela.id,
    tipo: q.regra.tipo, pct_padrao: pctOuNulo(q.regra.pctPadrao), gatilho_liberacao: q.regra.gatilho,
  } as RegraComissao // so id/tipo/pct_padrao sao lidos por pctParaProduto
  return { regra, percentuais: fonte ? vivosDa(s, fonte.id) : [] }
}

export function pctMostrado(s: SalvoDaTabela, q: QuadroDaTabela, produto: Produto): number | null {
  const v = emVigor(s, q)
  return v ? pctParaProduto(v.regra, v.percentuais, produto) : null
}

export function textoPct(n: number | null): string {
  return n == null ? '' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 6 })
}
export function textoPreco(n: number | null | undefined): string {
  return n == null ? '' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function valoresDaLinha(s: SalvoDaTabela, q: QuadroDaTabela, produto: Produto, e: EdicaoDaLinha | undefined) {
  const salvo = s.precos.find((p) => p.produto_id === produto.id)
  return {
    preco: e?.preco ?? textoPreco(salvo?.preco),
    avulso: e?.avulso ?? textoPreco(salvo?.preco_unidade),
    comissao: e?.comissao ?? textoPct(pctMostrado(s, q, produto)),
  }
}

const idsNaTabela = (s: SalvoDaTabela) => new Set(s.precos.map((p) => p.produto_id))
// Fica na tabela depois de salvar: ja estava, ou ganhou um preco agora.
const ficaNaTabela = (naTabela: Set<string>, id: string, e: EdicaoDaLinha | undefined) =>
  naTabela.has(id) || (e?.preco !== undefined && e.preco.trim() !== '')

export function haSemComissao(s: SalvoDaTabela, q: QuadroDaTabela): boolean {
  const na = idsNaTabela(s)
  return s.produtos.some((p) => na.has(p.id) && pctMostrado(s, q, p) === null)
}

export function produtosDoFiltro(s: SalvoDaTabela, q: QuadroDaTabela, busca: string, filtro: FiltroDaTabela, novoId?: string | null): Produto[] {
  const lista = produtosParaEditarPreco(s.produtos, idsNaTabela(s), busca)
  // "Fora da tabela" so lista com busca (teste dela 29/09): o catalogo inteiro
  // sem preco parecia "os precos sumiram". O recem-criado aparece sozinho.
  const semBusca = filtro === 'fora' && busca.trim() === ''
  if (semBusca) return lista.foraDaTabela.filter((p) => p.id === novoId)
  const base = filtro === 'fora' ? lista.foraDaTabela
    : filtro === 'nesta' ? lista.naTabela
    : lista.naTabela.filter((p) => pctMostrado(s, q, p) === null)
  // Decisao dela 01/10: incompletos no TOPO (fora da tabela nao tem preco por
  // definicao -- la nao se reordena).
  const ordenada = filtro === 'fora' ? base
    : [...base.filter((p) => linhaIncompleta(s, q, p)), ...base.filter((p) => !linhaIncompleta(s, q, p))]
  if (!novoId) return ordenada
  const novo = ordenada.find((p) => p.id === novoId)
  return novo ? [novo, ...ordenada.filter((p) => p.id !== novoId)] : ordenada
}

// Produto da tabela sem as informacoes basicas (decisao dela 01/10): card
// VERMELHO CLARO e topo da lista. Basicas: preco > 0; comissao (o motor pagaria
// algo); tipo de embalagem; e CX/FD com Qtd por Emb. Medido 01/10: exigir Qtd de
// todos pintaria 86% da lista -- "UN." e "DZ" nao tem Qtd e estao certos.
export type FaltaDaLinha = 'preco' | 'comissao' | 'tipo' | 'qtd'

// O QUE falta (teste dela 01/10: o 1001 da MAFFER estava vermelho com preco e
// comissao, e nada dizia que era a Qtd da caixa).
export function faltasDaLinha(s: SalvoDaTabela, q: QuadroDaTabela, produto: Produto): FaltaDaLinha[] {
  const faltas: FaltaDaLinha[] = []
  const salvo = s.precos.find((p) => p.produto_id === produto.id && p.apagado_em === null)
  if (!salvo || !(salvo.preco > 0)) faltas.push('preco')
  if (pctMostrado(s, q, produto) == null) faltas.push('comissao')
  const embalagem = faltaDeEmbalagem(produto)
  if (embalagem) faltas.push(embalagem)
  return faltas
}

export function linhaIncompleta(s: SalvoDaTabela, q: QuadroDaTabela, produto: Produto): boolean {
  return faltasDaLinha(s, q, produto).length > 0
}

export function errosDaTabela(s: SalvoDaTabela, q: QuadroDaTabela, e: EdicoesDaTabela): Record<string, string> {
  const erros: Record<string, string> = {}
  if (q.regra && q.regra.tipo === 'pct_fixo' && converterComissao(q.regra.pctPadrao) === null) erros[CHAVE_QUADRO] = MSG_PCT_FIXO_VAZIO
  else if (q.regra && q.regra.pctPadrao.trim() !== '' && converterComissao(q.regra.pctPadrao) === null) erros[CHAVE_QUADRO] = MSG_COMISSAO
  const quadroRuim = CHAVE_QUADRO in erros
  const na = idsNaTabela(s)
  for (const produto of s.produtos) {
    const ed = e[produto.id]
    if (!ficaNaTabela(na, produto.id, ed)) continue
    if (ed?.preco !== undefined) {
      if (ed.preco.trim() === '') { erros[produto.id] = MSG_PRECO_VAZIO; continue }
      if (precoDigitado(ed.preco) === null) { erros[produto.id] = MSG_PRECO_INVALIDO; continue }
    }
    if (ed?.avulso !== undefined && precoAvulsoDigitado(ed.avulso) === undefined) { erros[produto.id] = MSG_AVULSO_INVALIDO; continue }
    if (quadroRuim) continue
    const texto = ed?.comissao ?? textoPct(pctMostrado(s, q, produto))
    if (converterComissao(texto) === null) erros[produto.id] = MSG_COMISSAO
  }
  return erros
}

// Teste dela 29/09 (itens 4 e 5): o que pede confirmacao antes de gravar --
// comissao 0% e preco que cai a menos da metade ou passa do dobro. Um texto por
// produto; lista vazia = grava sem perguntar.
interface Avisado { produtoId: string; campo: 'preco' | 'comissao'; texto: string }

function avisados(s: SalvoDaTabela, q: QuadroDaTabela, e: EdicoesDaTabela): Avisado[] {
  const avisos: Avisado[] = []
  const na = idsNaTabela(s)
  const reais = (n: number) => `R$ ${textoPreco(n)}`
  for (const produto of s.produtos) {
    const ed = e[produto.id]
    if (!ed || !ficaNaTabela(na, produto.id, ed)) continue
    const existente = s.precos.find((p) => p.produto_id === produto.id)
    const valor = ed.preco === undefined ? null : precoDigitado(ed.preco)
    // Decisao dela 30/09: avisa em TODA mudanca de preco (antes: so metade/dobro).
    if (existente && valor !== null && valor !== existente.preco) {
      avisos.push({ produtoId: produto.id, campo: 'preco', texto: `${produto.codigo}: preço de ${reais(existente.preco)} para ${reais(valor)}` })
    }
    const pct = ed.comissao !== undefined ? converterComissao(ed.comissao) : existente ? undefined : pctMostrado(s, q, produto)
    if (pct === 0) avisos.push({ produtoId: produto.id, campo: 'comissao', texto: `${produto.codigo}: comissão 0%` })
  }
  return avisos
}

export function avisosAoSalvar(s: SalvoDaTabela, q: QuadroDaTabela, e: EdicoesDaTabela): string[] {
  return avisados(s, q, e).map((a) => a.texto)
}

// Teste dela 30/09 (opcao A): Cancelar no aviso volta SO o campo que avisou.
// Produto novo que entraria com 0% herdado nao tem comissao digitada para
// voltar: a linha inteira sai (ele continua fora da tabela, como estava).
export function desfazerAvisados(s: SalvoDaTabela, q: QuadroDaTabela, e: EdicoesDaTabela): EdicoesDaTabela {
  const novo: EdicoesDaTabela = { ...e }
  for (const a of avisados(s, q, e)) {
    const ed = novo[a.produtoId]
    if (!ed) continue
    if (a.campo === 'comissao' && ed.comissao === undefined) { delete novo[a.produtoId]; continue }
    const { [a.campo]: _, ...resto } = ed
    if (Object.keys(resto).length === 0) delete novo[a.produtoId]
    else novo[a.produtoId] = resto
  }
  return novo
}

// O "Preco anterior" embaixo do campo (item 5): enquanto edita, o gravado; sem
// edicao, o que ficou guardado no ultimo Salvar.
export function precoAnteriorDaLinha(s: SalvoDaTabela, produto: Produto, e: EdicaoDaLinha | undefined): number | null {
  const salvo = s.precos.find((p) => p.produto_id === produto.id)
  if (!salvo) return null
  if (e?.preco !== undefined && precoDigitado(e.preco) !== salvo.preco) return salvo.preco
  return salvo.preco_anterior ?? null
}

function mesmoQuadro(a: QuadroDaTabela, b: QuadroDaTabela): boolean {
  const ordem = (l: string[]) => [...l].sort().join('|')
  const r = (x: RegraRascunho | null) => (x === null ? 'null' : `${x.id}|${x.tipo}|${x.pctPadrao.trim()}|${x.gatilho}`)
  return a.ativa === b.ativa && ordem(a.condicoes) === ordem(b.condicoes) && r(a.regra) === r(b.regra)
}

export function temAlteracao(s: SalvoDaTabela, q: QuadroDaTabela, e: EdicoesDaTabela): boolean {
  return !mesmoQuadro(q, quadroInicial(s)) || Object.values(e).some((ed) => Object.values(ed).some((v) => v !== undefined))
}

export function gravacoesDaTabela(s: SalvoDaTabela, q: QuadroDaTabela, e: EdicoesDaTabela, novo: Novo, agora: string): GravacaoDaTabela[] {
  if (Object.keys(errosDaTabela(s, q, e)).length > 0) throw new Error('gravacoesDaTabela chamada com erro pendente')
  const saida: GravacaoDaTabela[] = []
  const t = s.tabela
  const na = idsNaTabela(s)

  // 1. a tabela (quadro)
  const ordem = (l: readonly string[]) => [...l].sort().join('|')
  if (q.ativa !== t.ativa || ordem(q.condicoes) !== ordem(t.condicoes_validas ?? [])) {
    saida.push({ tabela: 'tabelas_preco', registro: { ...t, ativa: q.ativa, condicoes_validas: [...q.condicoes] } })
  }

  // 2. comissoes mudadas DE VERDADE, so de quem fica na tabela
  const alteradas: Array<{ produto: Produto; pct: number }> = []
  for (const produto of s.produtos) {
    const ed = e[produto.id]
    if (ed?.comissao === undefined || !ficaNaTabela(na, produto.id, ed)) continue
    const pct = converterComissao(ed.comissao)!
    if (pct !== pctMostrado(s, q, produto)) alteradas.push({ produto, pct })
  }

  // 3. a regra da tabela
  let final = q.regra
  if (alteradas.length > 0 && final === null) final = regraPropriaCopiandoFornecedora(s)
  if (alteradas.length > 0 && final !== null && final.tipo === 'pct_fixo') final = { ...final, tipo: 'pct_por_item' }
  const propria = regraPropria(s)
  if (final === null) {
    if (propria) saida.push({ tabela: 'regras_comissao', registro: { ...propria, apagado_em: agora } })
  } else {
    const forn = regraDaFornecedora(s)
    const pct_padrao = pctOuNulo(final.pctPadrao)
    // Regra "nova" (id null) = copia da fornecedora. Se a tabela JA tem regra
    // propria viva, ela e REAPROVEITADA: o indice 0008 so aceita uma regra
    // viva por tabela, e apagar+criar chegaria fora de ordem na fila.
    const recopiar = final.id === null || !propria
    let regraId: string
    if (propria) {
      regraId = propria.id
      if (propria.tipo !== final.tipo || propria.pct_padrao !== pct_padrao || propria.gatilho_liberacao !== final.gatilho) {
        saida.push({ tabela: 'regras_comissao', registro: { ...propria, tipo: final.tipo, pct_padrao, gatilho_liberacao: final.gatilho } })
      }
    } else {
      const nova: RegraComissao = {
        ...novo(), representada_id: t.representada_id, tabela_preco_id: t.id,
        tipo: final.tipo, pct_padrao, gatilho_liberacao: final.gatilho,
      }
      regraId = nova.id
      saida.push({ tabela: 'regras_comissao', registro: nova })
    }
    // Numa regra "% fixo" os % por produto sao inertes: nao se copiam nem se
    // mexem. Na "por produto", a regra fica EXATAMENTE com o que a tela
    // mostrava + o que mudou -- % antigo guardado sob um "% fixo" (que a tela
    // nao mostrava) nao pode voltar a valer em silencio (revisao final, 29/09).
    if (final.tipo === 'pct_por_item') {
      const atuais = propria ? vivosDa(s, propria.id) : []
      const fonte = recopiar ? (forn?.tipo === 'pct_por_item' ? vivosDa(s, forn.id) : []) : (propria!.tipo === 'pct_por_item' ? atuais : [])
      const desejados = new Map<string, number>(fonte.map((c) => [c.alvo, c.pct]))
      for (const a of alteradas) desejados.set(`produto:${a.produto.id}`, a.pct)
      for (const c of atuais) {
        const d = desejados.get(c.alvo)
        if (d === undefined) saida.push({ tabela: 'comissao_percentuais', registro: { ...c, apagado_em: agora } })
        else if (d !== c.pct) saida.push({ tabela: 'comissao_percentuais', registro: { ...c, pct: d } })
        desejados.delete(c.alvo)
      }
      for (const [alvo, pct] of desejados) saida.push({ tabela: 'comissao_percentuais', registro: { ...novo(), regra_id: regraId, alvo, pct } })
    }
  }

  // 4. precos. ⚠ R12: a chave do avulso so entra se o campo foi MEXIDO.
  for (const produto of s.produtos) {
    const ed = e[produto.id]
    if (!ed || (ed.preco === undefined && ed.avulso === undefined)) continue
    const existente = s.precos.find((p) => p.produto_id === produto.id)
    const valor = ed.preco === undefined ? null : precoDigitado(ed.preco)
    if (!existente && valor === null) continue // avulso sozinho nao poe na tabela
    const avulso = ed.avulso === undefined ? undefined : precoAvulsoDigitado(ed.avulso)
    const mudouPreco = valor !== null && valor !== existente?.preco
    const mudouAvulso = avulso !== undefined && avulso !== (existente?.preco_unidade ?? null)
    if (existente && !mudouPreco && !mudouAvulso) continue
    const registro: Preco = {
      ...(existente ?? { ...novo(), tabela_preco_id: t.id, produto_id: produto.id, preco: 0, quantidade_minima: null, quantidade_maxima: null, preco_anterior: null }),
      ...(valor === null ? {} : { preco: valor }),
      ...(existente && mudouPreco ? { preco_anterior: existente.preco } : {}),
      ...(avulso === undefined ? {} : { preco_unidade: avulso }),
    }
    saida.push({ tabela: 'precos', registro })
  }
  return saida
}
